import { Component, ChangeDetectionStrategy, input, TemplateRef, EventEmitter, output } from '@angular/core';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { TableColumn } from './table.model';
import { NgTemplateOutlet } from '@angular/common';

/**
 * TableComponent
 * This component renders a customizable table with various input properties to manage
 * appearance, behavior, and data. It also includes functionalities for item selection
 * and pagination.
 *
 * @template T - The type of data displayed in the table rows.
 */
@Component({
  selector: 'ui-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTableModule, NgTemplateOutlet],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T extends { id?: number; disabled?: boolean, [key: string]: any }> {

  /**
   * The data source for the table rows.
   */
  dataSource = input<T[]>([]);

  /**
   * The configuration of columns to display in the table.
   */
  columns = input<TableColumn[]>([]);

  /**
   * A mapping of column keys to their corresponding templates for custom rendering.
   */
  columnTemplates = input<{ [key: string]: TemplateRef<any> }>({});

  /**
   * Whether the table supports multiple row selection.
   */
  isMultiSelect = input<boolean>(false);

  /**
   * Whether the table is currently in a loading state.
   */
  loading = input<boolean>(false);

  /**
   * The size of the table. Options include 'default', 'middle', and 'small'.
   */
  size = input<'default' | 'middle' | 'small'>('default');

  /**
   * Whether to display Pagination total number and range, same as Pagination
   */
  showTotal = input<TemplateRef<{ $implicit: number, range: [ number, number ] }> | null>(null);

  /**
   * Whether to display pagination controls for the table.
   */
  showPagination = input<boolean>(true);

  /**
   * Whether to select pagination for the table.
   */
  showPageSizeChanger = input<boolean>(false);

  /**
   * Whether the table should have borders around its cells.
   */
  bordered = input<boolean>(false);

  /**
   * The total number of items for pagination.
   */
  total = input<number>(0);

  /**
   * The size of pagination
   */
  paginationType = input<'default' | 'small'>('default');

  /**
   * The number of items to display per page.
   */
  pageSize = input<number>(10);

  /**
   * The page index to display.
   */
  pageIndex = input<number>(1);

  /**
   * The front pagination.
   */
  frontPagination = input<boolean>(true);

  /**
   * Configuration for scrollable table behavior. Accepts optional horizontal (x) and vertical (y) dimensions.
   */
  scrollConfig = input<{ x?: string | null; y?: string | null }>({});

  /**
   * Template to display when the table has no data.
   */
  emptyStateTemplate = input<any>(null);

  /**
   * Indicates whether all items on the current page are checked.
   */
  checked = false;

  /**
   * Indicates whether some items are checked, but not all.
   */
  indeterminate = false;

  /**
   * A set containing the IDs of the currently checked items.
   */
  setOfCheckedId = new Set<number>();

  /**
   * The data currently displayed on the visible page.
   */
  listOfCurrentPageData: readonly T[] = [];

  /**
   * Template to display when the table has no data.
   */
  onQueryParams = output<any>();

  /**
   * A set containing the indexs of the currently checked items.
   */
  onChecked = output<number[]>();

  /**
   * Determines if the table is in an empty state (i.e., no data in the dataSource).
   */
  get showEmptyState(): boolean {
    return this.dataSource().length === 0;
  }

  /**
   * Updates the list of data displayed on the current page and refreshes the checked status.
   *
   * @param listOfCurrentPageData - The data displayed on the current page.
   */
  onCurrentPageDataChange(listOfCurrentPageData: readonly T[]): void {
    this.listOfCurrentPageData = listOfCurrentPageData;
    this.refreshCheckedStatus();
  }

  /**
   * Emits the provided query parameters to trigger the query params update.
   *
   * @param queryParams - The query parameters to be passed for the table query.
   *   This object typically includes information like pagination, sorting, and filter criteria.
   * @returns void
   *
   * @example
   * // Emits the query params object to the parent component
   * this.sendQueryParams({ pageIndex: 1, pageSize: 10, sort: 'asc' });
   */
  sendQueryParams(queryParams: NzTableQueryParams): void {
    this.onQueryParams.emit(queryParams);
  }

  /**
   * Refreshes the checked and indeterminate statuses based on the current page's data.
   * This ensures the UI reflects the correct state for checkboxes.
   */
  refreshCheckedStatus(): void {
    const listOfEnabledData = this.listOfCurrentPageData.filter((item: any) => !item.disabled);
    this.checked = listOfEnabledData.every((item: any) => this.setOfCheckedId.has(item.id));
    this.indeterminate =
      listOfEnabledData.some((item: any) => this.setOfCheckedId.has(item.id)) && !this.checked;

    this.onChecked.emit([...this.setOfCheckedId])
  }

  /**
   * Updates the checked status of a single item based on user interaction.
   *
   * @param id - The unique identifier of the item.
   * @param checked - Whether the item is checked.
   */
  onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  /**
   * Updates the checked status of all items on the current page.
   *
   * @param checked - Whether all items on the current page should be checked.
   */
  onAllChecked(checked: boolean): void {
    this.listOfCurrentPageData
      .filter((item: any) => !item.disabled)
      .forEach((item: any) => this.updateCheckedSet(item.id, checked));
    this.refreshCheckedStatus();
  }

  /**
   * Adds or removes an item ID from the set of checked items.
   *
   * @param id - The unique identifier of the item.
   * @param checked - Whether the item should be marked as checked.
   */
  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }
}
